import { useState, type FormEvent } from "react";
import { DEFAULT_AVATAR, type Conversation } from "../api/client";
import { formatDayShort, formatTimeParts } from "../lib/format";

export type ChatMessage = NonNullable<Conversation["messages_list"]>[number];

export type ChatBubbleProps = {
  message: ChatMessage;
};

export function ChatBubble({ message }: ChatBubbleProps) {
  const isStaff =
    message.sender_info?.meta_type != null &&
    message.sender_info.meta_type !== "Patient";
  const { time, period } = formatTimeParts(message.created_at);

  return (
    <li
      className={`ne__chat__item message-group${
        isStaff ? " ne__chat__item--staff" : ""
      }`}
    >
      <div className="ne__chat__avatar">
        <figure className="ne__avatar">
          <img src={message.sender_info?.image || DEFAULT_AVATAR} alt="" />
        </figure>
      </div>
      <ul className="ne__chat__message-group">
        <p className="ne__chat__author">
          {message.sender_info?.name || "User"}
        </p>
        <li className="ne__chat__message">
          <div className="ne__chat__bubble">
            <p className="ne__chat__text">{message.body}</p>
          </div>
          <p className="ne__chat__sent-date">
            <span>{formatDayShort(message.created_at)}</span>
            <span> at </span>
            <span>{time}</span>
            <span className="ne__period">{period}</span>
          </p>
        </li>
      </ul>
    </li>
  );
}

export type ChatThreadProps = {
  messages: ChatMessage[];
  draft?: string;
  onDraftChange?: (value: string) => void;
  onSend?: (value: string) => void;
  showComposer?: boolean;
};

export function ChatThread({
  messages,
  draft: controlledDraft,
  onDraftChange,
  onSend,
  showComposer = true,
}: ChatThreadProps) {
  const [internalDraft, setInternalDraft] = useState("");
  const draft = controlledDraft ?? internalDraft;
  const setDraft = onDraftChange ?? setInternalDraft;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!draft.trim()) return;
    onSend?.(draft.trim());
    setDraft("");
  }

  return (
    <div className="ne__patient__tab ne__patient__tab--messages" id="messages-view">
      <div className="ne__chat">
        <div className="ne__chat__box">
          <div className="ne__chat__scroll">
            <ul className="conversation ne__chat__list">
              {messages.map((message) => (
                <ChatBubble key={message.id} message={message} />
              ))}
            </ul>
          </div>
          <div className="ne__chat__mask" />
        </div>
        {showComposer ? (
          <div className="ne__send-msg">
            <div className="ne__send-msg__message">
              <form className="ne__send-msg__form" onSubmit={handleSubmit}>
                <textarea
                  className="ne__textarea ne__send-msg__input"
                  rows={2}
                  placeholder="Write a message…"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                />
                <button
                  type="submit"
                  className="ne__btn ne__btn--primary ne__btn--md"
                  disabled={!draft.trim()}
                >
                  Send
                </button>
              </form>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
